import { InvoiceDueInformation } from './invoice-due-information';
import { BusinessEntity, DateService, NULL_DATA_STRING } from '@aiwyn/shared';
import { AutoPay, BaseEngagement, Currency, Invoice, PayableUi, Staff } from '@aiwyn/api';

export enum PayableType {
  INVOICE = 'INVOICE',
  PREPAYMENT = 'PREPAYMENT',
}

export abstract class PayableTableObject {

  static create(
    payableUi: PayableUi,
    autoPayClients: AutoPay[],
    businessEntity: BusinessEntity,
    dueUponReceipt: boolean = false
  ): PayableTableObject {
    if ('engagement' in payableUi) {
      return new PrepaymentTableObject(
        payableUi,
        false,
        payableUi.payable,
        this.invoiceDueDateHelper(payableUi.payable.dueDate, dueUponReceipt),
        payableUi.responsibleStaff,
        businessEntity?.name,
        businessEntity?.id,
        businessEntity?.autopayEligible,
        payableUi.autoPayInvoice?.amount,
        autoPayClients?.find(autoPayClient => autoPayClient.clientId === payableUi?.client?.id)?.amount || 0,
        payableUi.autoPayInvoice?.autoExecutionDate,
        payableUi.autoPayInvoice?.bufferDays,
        payableUi.autoPayInvoice?.partialPayment,
        payableUi.engagement,
      );
    }

    return new InvoiceTableObject(
      payableUi,
      false,
      payableUi.payable,
      this.invoiceDueDateHelper(payableUi.payable.dueDate, dueUponReceipt),
      payableUi.responsibleStaff,
      businessEntity?.name,
      businessEntity?.id,
      businessEntity?.autopayEligible,
      payableUi?.autoPayInvoice?.amount,
      autoPayClients?.find(autoPayClient => autoPayClient.clientId === payableUi?.client?.id)?.amount || 0,
      payableUi?.autoPayInvoice?.autoExecutionDate,
      payableUi?.autoPayInvoice?.bufferDays,
      payableUi?.autoPayInvoice?.partialPayment,
      false,
      payableUi?.autoPayInvoice?.daysToExecute,
      payableUi?.currency
    );
  }

  /**
   * Helper function that uses netTerm days and invoiceDate to calculate:
   * isOverdue, displayStatus and invoiceDueDate.
   * Returns as InvoiceDueDateInformation type.
   */

  static invoiceDueDateHelper(dueDate: Date, dueUponReceipt: boolean = false): InvoiceDueInformation {
    if (!dueDate) {
      return new InvoiceDueInformation(false, NULL_DATA_STRING, NULL_DATA_STRING);
    }
    // Finds difference between currentDate and invoiceDueDate to calculate number of days until due/overdue and coverts ms to days.
    const days = DateService.daysTillDueDate(dueDate);
    // const days = 1;
    let daysUntilDue: string;

    // If statement used to created strings for displayStatus with correct language context, i.e. day not days when applicable.
    // Positive numbers represent overdue invoices, negative numbers represent due invoices and zero indicates due today.
    if (days > 1) {
      // Overdue by X days.
      daysUntilDue = 'by ' + days + ' days';
    } else if (days === 1) {
      // Overdue by 1 day.
      daysUntilDue = 'by ' + days + ' day';
    } else if (days === 0) {
      // Due today.
      daysUntilDue = 'today';
    } else if (days === -1) {
      // Due in 1 day.
      daysUntilDue = 'in ' + Math.abs(days) + ' day';
    } else if (days < -1) {
      // Due in X days.
      daysUntilDue = 'in ' + Math.abs(days) + ' days';
    }


    const day = dueDate.getDate().toString().length > 1 ? dueDate.getDate().toString()
      : '0' + dueDate.getDate().toString();
    const month = (dueDate.getMonth() + 1).toString();
    const year = dueDate.getFullYear().toString().slice(2);
    return new InvoiceDueInformation((days > 0), dueUponReceipt ? 'Due upon receipt' : daysUntilDue, month + '/' + day + '/' + year);
  }

  checked: boolean;
  // Make it easier to search tables by 'due' or 'overdue'
  statusSearchString: string;
  overdue: boolean;
  // Status with date to display in table.
  displayStatus: string;
  // If a dueDate is provided with the ArTran we will use that, otherwise we will use net_terms from config.
  dueDateDisplay: string;
  invoice: Invoice;
  responsibleStaff: Staff;
  clientName: string;
  clientId: number;
  entityName: string;
  entityId: number;
  entityAutopayEnabled: boolean | undefined;
  disabled: boolean;
  amount: number;
  maxAmount: number;
  autoExecutionDate: number[];
  bufferDays: number;
  autoPayDueIn: number;
  partialPayment: boolean;
  currency: Currency;

  constructor(
    payable: PayableUi,
    checked: boolean,
    invoice: Invoice,
    invoiceDueInformation: InvoiceDueInformation,
    responsibleStaff: Staff,
    entityName: string,
    entityId: number,
    entityAutopayEnabled: boolean | undefined,
    amount: number,
    maxAmount: number,
    autoExecutionDate: number[],
    bufferDays: number,
    partialPayment: boolean,
    disabled: boolean = false,
    autoPayDaysToExecute: number = 0,
    currency: Currency = null,
  ) {
    this.checked = checked;
    this.overdue = invoiceDueInformation.isOverdue;
    this.displayStatus = invoiceDueInformation.displayStatus;
    this.dueDateDisplay = invoiceDueInformation.invoiceDueDate;
    this.invoice = invoice;
    this.responsibleStaff = responsibleStaff;
    this.statusSearchString = this.setStatusSearchString(this.overdue, invoice.arTran.amount === invoice.arTran.allocated);
    this.entityName = entityName;
    this.entityId = entityId;
    this.entityAutopayEnabled = entityAutopayEnabled;
    this.amount = amount;
    this.maxAmount = maxAmount;
    this.autoExecutionDate = autoExecutionDate;
    this.bufferDays = bufferDays;
    this.partialPayment = partialPayment;
    this.disabled = disabled;
    this.clientName = payable.client.name;
    this.clientId = payable.client.id;
    this.currency = currency;

    if (!!autoExecutionDate && !!bufferDays) {
      // const autoExecuteDateParts: string[] = autoExecutionDate.split('-');
      // const autoExecuteDateObj: Date = new Date(+autoExecuteDateParts[0], +autoExecuteDateParts[1], +autoExecuteDateParts[2]);
      // Months in Javascript are 0 based when creating new dates
      if (autoPayDaysToExecute) {
        this.autoPayDueIn = autoPayDaysToExecute;
        return;
      }
      const autoExecuteDateObj: Date = new Date(+autoExecutionDate[0], (+autoExecutionDate[1] - 1), +autoExecutionDate[2]);
      const dueInDays = Math.ceil((autoExecuteDateObj.getTime() - new Date().getTime()) / (1000 * 3600 * 24));
      this.autoPayDueIn = dueInDays <= bufferDays ? dueInDays : dueInDays + bufferDays;
    }
  }

  setStatusSearchString(overdue: boolean, paid: boolean): string {
    if (!paid) {
      if (overdue) {
        return 'overdue';
      } else {
        return 'due';
      }
    } else {
      return 'paid';
    }
  }

  abstract getType(): PayableType;
  abstract getSummaryTitle(): string;
  abstract get dueDate(): Date;
  abstract getReviewTitle(): string;
  abstract getInvoiceDate(): Date;
  abstract canPayPartially(): boolean;
  abstract getResourceId(): number;
  abstract get invoiceNumber(): string;

  get arTranId(): number {
    return this.invoice.arTran.id;
  }

  get amountRemaining(): number {
    return this.invoice.arTran.amount - this.invoice.arTran.allocated;
  }
}

export class InvoiceTableObject extends PayableTableObject {
  get invoiceNumber(): string {
    return this.invoice.invoiceNumber;
  }

  getType(): PayableType {
    return PayableType.INVOICE;
  }

  getSummaryTitle(): string {
    return `Inv ${this.invoice.invoiceNumber}`;
  }

  get dueDate(): Date {
    return this.invoice.dueDate;
  }

  getReviewTitle(): string {
    return `Invoice ${this.invoice.invoiceNumber}`;
  }

  getInvoiceDate(): Date {
    return this.invoice.arTran.transactionDate;
  }

  canPayPartially(): boolean {
    return true;
  }

  getResourceId(): number {
    return this.invoice.arTran.id;
  }
}

export class PrepaymentTableObject extends PayableTableObject {
  engagement: BaseEngagement;

  constructor(
    payableUi: PayableUi,
    checked: boolean,
    invoice: Invoice,
    invoiceDueInformation: InvoiceDueInformation,
    responsibleStaff: Staff,
    entityName: string,
    entityId: number,
    entityAutopayEnabled: boolean | undefined,
    amount: number,
    maxAmount: number,
    autoExecutionDate: number[],
    bufferDays: number,
    partialPayment: boolean,
    engagement: BaseEngagement,
    disabled: boolean = false
  ) {
    super(
      payableUi,
      checked,
      invoice,
      invoiceDueInformation,
      responsibleStaff,
      entityName,
      entityId,
      entityAutopayEnabled,
      amount,
      maxAmount,
      autoExecutionDate,
      bufferDays,
      partialPayment,
      disabled
    );

    this.engagement = engagement;
  }

  get invoiceNumber(): string {
    return `Engagement ${this.engagement?.id}`;
  }

  getType(): PayableType {
    return PayableType.PREPAYMENT;
  }

  getSummaryTitle(): string {
    return `Engagement ${this.engagement?.id}`;
  }

  get dueDate(): Date {
    return this.invoice.dueDate;
  }

  getReviewTitle(): string {
    return `Prepayment for ${this.engagement?.name}`;
  }

  getInvoiceDate(): Date {
    return this.engagement?.executedAt;
  }

  canPayPartially(): boolean {
    return false;
  }

  getResourceId(): number {
    return this.engagement?.id;
  }
}
